import React from 'react';
import {
  Button, IconButton, Panel, withDialogs, WithDialogsProps,
} from '@michelin/acid-components';
import { Grid } from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';
import { getOptions, SelectOption } from '@michelin/select-options-provider';
import { withTranslation, WithTranslation } from '@michelin/central-provider';
import uuid from 'uuid';
import { cloneDeep } from 'lodash';

import { DealerAutocomplete } from 'components/Autocomplete';
import { sortOptions } from 'Utils';
import { PlainMessage } from '../../PlainMessage';
import {
  Contact,
  ContactType,
  ContactTypeRole,
  ContactTypeRoleLevels,
  SPPContactTypeLevelsEnum,
  SPPContactTypeRoleAreas,
  SPPContactTypeRoleAreasEnum,
  SPPContactTypeServicesEnum,
} from '../../../Types';
import { PriorityLevelsManagementModal } from './PriorityLevelsManagementModal';
import { hasContactTypePriorityLevelsAssigned, hasMichelinContactTypes } from '../Utils';

interface ContactTypesListProps extends WithDialogsProps, WithTranslation {
  contact: Contact,
  stNumber: string,
  btNumber: string,
  countryCode: string,
  action: string,
  hasCertificates: boolean,
  hasOutsideCertificates: boolean,
  onChange?: (contactTypes: ContactType[]) => void,
  afterSave: () => void,
  readOnly: boolean,
}

interface ContactTypesListState {
  listData: ContactType[],
  assignModalContact: Contact | null,
  reassignModalContact: Contact | null,
  forceReassign: boolean,
  hasCertificates: boolean,
  hasOutsideCertificates: boolean,
}

class ContactTypesList extends React.Component<ContactTypesListProps, ContactTypesListState> {
  originalData: ContactType[];

  constructor(props: ContactTypesListProps) {
    super(props);

    this.originalData = (this.props.contact.contact_types) ? this.completeContactTypes(this.props.contact.contact_types) : this.getEmptyContactTypes();

    this.state = {
      listData: cloneDeep(this.originalData).sort((a, b) => {
        if (a.service < b.service) return -1;
        if (a.service > b.service) return 1;
        return 0;
      }),
      assignModalContact: null,
      reassignModalContact: null,
      forceReassign: false,
      hasCertificates: (this.props.contact.certifications?.length || 0) > 0,
      hasOutsideCertificates: (this.props.contact.outside_certifications?.length || 0) > 0,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
  shouldComponentUpdate(nextProps: ContactTypesListProps, nextState: ContactTypesListState) {
    if (this.props.hasCertificates !== nextProps.hasCertificates) {
      this.setState({ hasCertificates: nextProps.hasCertificates });
      this.forceUpdate();
    }
    if (this.props.hasOutsideCertificates !== nextProps.hasOutsideCertificates) {
      this.setState({ hasOutsideCertificates: nextProps.hasOutsideCertificates });
      this.forceUpdate();
    }

    if (this.props.contact !== nextProps.contact && nextProps.action === 'view') {
      this.originalData = (nextProps.contact.contact_types) ? this.completeContactTypes(nextProps.contact.contact_types) : this.getEmptyContactTypes();

      const newState: any = {};
      newState.listData = cloneDeep(this.originalData).sort((a, b) => {
        if (a.service < b.service) return -1;
        if (a.service > b.service) return 1;
        return 0;
      });

      this.setState(newState);
      this.forceUpdate();
    }
    return true;
  }

  // eslint-disable-next-line react/sort-comp
  completeContactTypes(contact_types: ContactType[]): ContactType[] {
    let commercial = false;
    let retail = false;
    contact_types.forEach((ct) => {
      if (ct.service === SPPContactTypeServicesEnum.commercial) commercial = true;
      if (ct.service === SPPContactTypeServicesEnum.retail) retail = true;
    });
    if (!commercial) contact_types.push(this.getEmptyContactType(SPPContactTypeServicesEnum.commercial));
    if (!retail) contact_types.push(this.getEmptyContactType(SPPContactTypeServicesEnum.retail));
    return contact_types;
  }

  getEmptyContactTypes() {
    return [this.getEmptyContactType(SPPContactTypeServicesEnum.commercial), this.getEmptyContactType(SPPContactTypeServicesEnum.retail)];
  }

  // eslint-disable-next-line class-methods-use-this
  getEmptyContactType(service: SPPContactTypeServicesEnum): ContactType {
    return { service, id: uuid.v4(), role_areas: [] };
  }

  renderPanelActions(): JSX.Element {
    const { t } = this.props;
    const readOnly: boolean = this.props.action === 'view' || this.props.action === 'add' || this.props.readOnly;

    return (
      <span style={{ position: 'relative', top: '4px' }}>
        {(readOnly || !this.hasContactTypePriorityLevels(this.state.listData) || this.isModified())
          ? null
          : (
            <Button
              size="small"
              color="primary"
              onClick={() => {
                this.setState({ assignModalContact: this.props.contact });
              }}
            >
              {t('Priority Levels')}
            </Button>
          )}
      </span>
    );
  }

  // eslint-disable-next-line react/sort-comp
  render() {
    const { t } = this.props;

    if (!this.state.listData) {
      return (
        <PlainMessage
          title={t('Error loading data')}
          messages={[t('Data object is empty...')]}
        />
      );
    }

    if (this.state.assignModalContact) {
      return (
        <PriorityLevelsManagementModal
          contactId={this.props.contact.id}
          contactLevel={this.props.contact.contact_level}
          firstName={this.props.contact.first_name}
          lastName={this.props.contact.last_name}
          stNumber={this.props.stNumber}
          btNumber={this.props.btNumber}
          countryCode={this.props.countryCode}
          action="assign"
          onClose={() => {
            this.setState({ assignModalContact: null });
          }}
          onSave={() => this.afterPriorityLevelsSave()}
          forceReassign={false}
        />
      );
    }

    if (this.state.reassignModalContact) {
      return (
        <PriorityLevelsManagementModal
          contactId={this.props.contact.id}
          contactLevel={this.props.contact.contact_level}
          firstName={this.props.contact.first_name}
          lastName={this.props.contact.last_name}
          stNumber={this.props.stNumber}
          btNumber={this.props.btNumber}
          countryCode={this.props.countryCode}
          action="reassign"
          onClose={() => {
            this.setState({ reassignModalContact: null });
          }}
          onSave={() => this.afterPriorityLevelsSave()}
          forceReassign={this.state.forceReassign}
        />
      );
    }

    return (
      <Panel
        title={`${t('Contact Types')} *`}
        control={this.renderPanelActions()}
      >
        <Grid
          container
          spacing={2}
          className={(!this.hasContactTypePriorityLevels(this.state.listData)) ? 'Mui-error' : undefined}
        >
          <Grid item xs={12}>
            {(this.props.action === 'view')
              ? this.renderContactTypesView()
              : this.renderContactTypesEdit()}
          </Grid>
        </Grid>
      </Panel>
    );
  }

  /// //////////////////////////////////////////////////////////////////
  /// Render View METHODS
  /// //////////////////////////////////////////////////////////////////

  renderContactTypesView(): JSX.Element {
    return (
      <Grid container spacing={2}>
        {this.state.listData.map((value: ContactType, index: number) => (this.renderServiceView(value, index)))}
      </Grid>
    );
  }

  renderServiceView(contactType: ContactType, index: number): JSX.Element {
    return (
      <Grid item xs={6} key={index}>
        <Panel
          title={this.getContactTypeServiceLabel(contactType)}
          spacing={2}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {this.getContactTypeRoleAreasLabels(contactType)}
            </Grid>
          </Grid>
        </Panel>
      </Grid>
    );
  }

  /// //////////////////////////////////////////////////////////////////
  /// Additional View METHODS
  /// //////////////////////////////////////////////////////////////////

  getContactTypeRoleAreasLabels(contactType: ContactType): JSX.Element {
    const { t } = this.props;

    if (!contactType.role_areas || !contactType.role_areas.length) return <b>{t('No Role/Areas for this Business')}</b>;

    return (
      <div>
        {contactType.role_areas.map((v: ContactTypeRole) => (
          <div key={v.role_area}>
            <b>{this.getContactTypeRoleAreaLabel(v)}</b>
            {' '}
            [
            {this.getContactTypeLevelLabel(v)}
            ]
          </div>
        ))}
      </div>
    );
  }

  /// //////////////////////////////////////////////////////////////////
  /// Render Edit METHODS
  /// //////////////////////////////////////////////////////////////////

  renderContactTypesEdit(): JSX.Element {
    return (
      <Grid container spacing={2}>
        {this.state.listData.map((value: ContactType, index: number) => (this.renderServiceEdit(value, index)))}
      </Grid>
    );
  }

  renderServiceEdit(contactType: ContactType, index: number): JSX.Element {
    const { t } = this.props;
    let roleAreasOptions = getOptions('dealer_dealer_contact_types_role_areas');

    roleAreasOptions = sortOptions(roleAreasOptions, false);

    roleAreasOptions = roleAreasOptions.map((value: SelectOption) => {
      const option: SelectOption = {
        label: `${value.label} [${this.getOptionLevelLabel(value, contactType)}]`,
        value: value.value,
      };
      return option;
    });

    const initial: string[] = [];
    const roleAreasSelectedValues = (!contactType.role_areas) ? [] : contactType.role_areas.reduce<string[]>(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
      (previousValue: string[], currentValue: ContactTypeRole, currentIndex: number, array: ContactTypeRole[]): string[] => {
        previousValue.push(currentValue.role_area);
        return previousValue;
      },
      initial,
    );

    return (
      <Grid item xs={6} key={index}>
        <Panel
          title={this.getContactTypeServiceLabel(contactType)}
          spacing={2}
          control={this.renderEditActions(contactType)}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DealerAutocomplete
                id={contactType.service}
                variant="standard"
                multiple
                disableClearable
                suggestions={roleAreasOptions}
                placeholder={t('Select Areas of Responsibility')}
                label={t('Role / Areas')}
                values={roleAreasSelectedValues}
                readOnly={this.props.readOnly}
                disableCloseOnSelect
                onChange={(v: any) => {
                  this.manageUpdateRoleAreasValue(v, contactType);
                }}
              />
            </Grid>
          </Grid>
        </Panel>
      </Grid>
    );
  }

  renderEditActions(contactType: ContactType): JSX.Element {
    const readOnly: boolean = this.props.action === 'view' || this.props.readOnly;

    if (readOnly) return <></>;

    const data: any = cloneDeep(contactType);
    // eslint-disable-next-line no-underscore-dangle
    delete data.__typename;

    return (
      <div>
        {(contactType.role_areas.length)
          ? (
            <IconButton
              aria-label="delete"
              style={{ margin: '8px' }}
              onClick={() => this.manageDeleteContactType(contactType)}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )
          : null}
      </div>
    );
  }

  /// //////////////////////////////////////////////////////////////////
  /// Additional Edit METHODS
  /// //////////////////////////////////////////////////////////////////

  // eslint-disable-next-line class-methods-use-this
  losingMichelinPriorityLevels(originalContactTypes: ContactType[], newContactTypes: ContactType[]): boolean {
    let countInitialPriorityLevels = 0;
    let countCurrentPriorityLevels = 0;

    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in originalContactTypes) {
      const cType = originalContactTypes[i];
      // eslint-disable-next-line no-restricted-syntax
      for (const j in cType.role_areas) {
        // eslint-disable-next-line max-len
        if ((cType.role_areas[j].role_area === SPPContactTypeRoleAreasEnum.pos_administrator) && cType.role_areas[j].levels && cType.role_areas[j].levels.length && cType.role_areas[j].levels[0].level !== SPPContactTypeLevelsEnum.general) countInitialPriorityLevels++;
      }
    }

    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in newContactTypes) {
      const cType = newContactTypes[i];
      // eslint-disable-next-line no-restricted-syntax
      for (const j in cType.role_areas) {
        // eslint-disable-next-line max-len
        if ((cType.role_areas[j].role_area === SPPContactTypeRoleAreasEnum.pos_administrator) && cType.role_areas[j].levels && cType.role_areas[j].levels.length && cType.role_areas[j].levels[0].level !== SPPContactTypeLevelsEnum.general) countCurrentPriorityLevels++;
      }
    }

    return countCurrentPriorityLevels < countInitialPriorityLevels;
  }

  // eslint-disable-next-line class-methods-use-this
  losingPriorityLevels(originalContactTypes: ContactType[], newContactTypes: ContactType[]): boolean {
    let countInitialPriorityLevels = 0;
    let countCurrentPriorityLevels = 0;

    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in originalContactTypes) {
      const cType = originalContactTypes[i];
      // eslint-disable-next-line no-restricted-syntax
      for (const j in cType.role_areas) {
        if (cType.role_areas[j].levels?.length && cType.role_areas[j].levels[0].level !== SPPContactTypeLevelsEnum.general) countInitialPriorityLevels++;
      }
    }

    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in newContactTypes) {
      const cType = newContactTypes[i];
      // eslint-disable-next-line no-restricted-syntax
      for (const j in cType.role_areas) {
        if (cType.role_areas[j].levels?.length && cType.role_areas[j].levels[0].level !== SPPContactTypeLevelsEnum.general) countCurrentPriorityLevels++;
      }
    }

    return countCurrentPriorityLevels < countInitialPriorityLevels;
  }

  losingCertificates(oldRoleAreas: SPPContactTypeRoleAreas[], newRoleAreas: SPPContactTypeRoleAreas[]): boolean {
    const hadTireTechnicianRoleAreas = oldRoleAreas.some((ra: SPPContactTypeRoleAreas) => ra === SPPContactTypeRoleAreasEnum.tire_technician);
    const hasTireTechnicianRoleAreas = newRoleAreas.some((ra: SPPContactTypeRoleAreas) => ra === SPPContactTypeRoleAreasEnum.tire_technician);
    const hadMechanicRoleAreas: boolean = oldRoleAreas.some((ra: SPPContactTypeRoleAreas) => ra === SPPContactTypeRoleAreasEnum.mechanic);
    const hasMechanicRoleAreas: boolean = newRoleAreas.some((ra: SPPContactTypeRoleAreas) => ra === SPPContactTypeRoleAreasEnum.mechanic);
    // eslint-disable-next-line max-len
    return ((hadTireTechnicianRoleAreas && !hasTireTechnicianRoleAreas && this.state.hasCertificates) || (hadMechanicRoleAreas && !hasMechanicRoleAreas && (this.state.hasCertificates || this.state.hasOutsideCertificates)));
  }

  // eslint-disable-next-line class-methods-use-this
  contactTypeIsLosingPriorityLevels(originalContactTypes: ContactType[], contactType: ContactType): boolean {
    let countInitialPriorityLevels = 0;
    let countCurrentPriorityLevels = 0;

    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in originalContactTypes) {
      const cType = originalContactTypes[i];
      if (cType.service !== contactType.service) continue;
      // eslint-disable-next-line no-restricted-syntax
      for (const j in cType.role_areas) {
        if (cType.role_areas[j].levels?.length && cType.role_areas[j].levels[0].level !== SPPContactTypeLevelsEnum.general) countInitialPriorityLevels++;
      }
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const j in contactType.role_areas) {
      if (contactType.role_areas[j].levels?.length && contactType.role_areas[j].levels[0].level !== SPPContactTypeLevelsEnum.general) countCurrentPriorityLevels++;
    }

    return countCurrentPriorityLevels < countInitialPriorityLevels;
  }

  async manageUpdateRoleAreasValue(value: SPPContactTypeRoleAreas[] | undefined, contactType: ContactType) {
    const { t } = this.props;

    const originalContactTypes: ContactType[] = (this.originalData) ? this.originalData : [];

    const initial: SPPContactTypeRoleAreas[] = [];
    const previousValue = (!contactType.role_areas) ? [] : contactType.role_areas.reduce<SPPContactTypeRoleAreas[]>(
      (previousValue: SPPContactTypeRoleAreas[], currentValue: ContactTypeRole): SPPContactTypeRoleAreas[] => {
        previousValue.push(currentValue.role_area);
        return previousValue;
      },
      initial,
    );

    const losingCertificates = this.losingCertificates(previousValue || [], value || []);
    if (losingCertificates) {
      this.props.confirmDialog(
        t('Certifications exist for {{contactFirstName}} {{contactLastName}} specific to Mechanic or Tire Technician and will be removed.', {
          contactFirstName: this.props.contact.first_name,
          contactLastName: this.props.contact.last_name,
        }),
        t('Losing Certificates'),
        t('Ok'),
        t('Cancel'),
        async () => {
          await this.updateRoleAreasValue(value, contactType);
        },
        undefined,
        { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
      );

      return;
    }

    await this.updateRoleAreasValue(value, contactType);

    const contactTypes: ContactType[] = (this.state.listData) ? this.state.listData : [];
    const losingMichelinPriorityLevels = this.losingMichelinPriorityLevels(originalContactTypes, contactTypes);
    const losingPriorityLevels = this.losingPriorityLevels(originalContactTypes, contactTypes);

    if (losingMichelinPriorityLevels) {
      this.props.confirmDialog(
        // eslint-disable-next-line max-len
        t('This Role / Area assigned to {{contactFirstName}} {{contactLastName}} has priority levels which must be reassigned to another contact at the location in order to complete this change. How do you wish to proceed?', {
          contactFirstName: this.props.contact.first_name,
          contactLastName: this.props.contact.last_name,
        }),
        t('Reassign priority levels and contact types'),
        t('Reassign'),
        t('Undo'),
        async () => {
          await this.updateRoleAreasValue(previousValue, contactType);
          await this.setState({ reassignModalContact: this.props.contact });
        },
        async () => {
          await this.updateRoleAreasValue(previousValue, contactType);
        },
        { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
      );
    } else if (losingPriorityLevels) {
      this.props.confirmDialog(
        // eslint-disable-next-line max-len
        t('This Role / Area assigned to {{contactFirstName}} {{contactLastName}} has priority levels which can be reassigned to another contact at the location. How do you wish to proceed?', {
          contactFirstName: this.props.contact.first_name,
          contactLastName: this.props.contact.last_name,
        }),
        t('Reassign priority levels and contact types'),
        t('Reassign'),
        t('Delete'),
        async () => {
          await this.updateRoleAreasValue(previousValue, contactType);
          await this.setState({ reassignModalContact: this.props.contact });
        },
        undefined,
        { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
      );
    }
  }

  async updateRoleAreasValue(value: SPPContactTypeRoleAreas[] | undefined, contactType: ContactType) {
    const data = cloneDeep(contactType);

    const roleAreas: ContactTypeRole[] = [];
    value?.forEach((area) => {
      const roleArea: ContactTypeRole = {
        role_area: area,
        levels: this.getExistingLevels(data, area),
      };
      roleAreas.push(roleArea);
    });

    data.role_areas = roleAreas;

    // eslint-disable-next-line react/no-access-state-in-setstate
    const contactTypes: ContactType[] = cloneDeep(this.state.listData);
    contactTypes.forEach((value: ContactType, index: number, array: ContactType[]) => {
      // eslint-disable-next-line no-param-reassign
      if (contactType.id === value.id) array[index] = data;
    });
    contactTypes.sort((a, b) => {
      if (a.service < b.service) return -1;
      if (a.service > b.service) return 1;
      return 0;
    });
    await this.setState({ listData: contactTypes });
    this.forceUpdate();
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.props.onChange && this.props.onChange(contactTypes);
  }

  // eslint-disable-next-line class-methods-use-this
  getExistingLevels(contactType: ContactType, role_area: string): ContactTypeRoleLevels[] {
    // eslint-disable-next-line no-restricted-syntax
    for (const i in contactType.role_areas) {
      if (contactType.role_areas[i].role_area === role_area) return contactType.role_areas[i].levels;
    }
    return [];
  }

  getOptionLevelLabel(option: SelectOption, contactType: ContactType) {
    // eslint-disable-next-line no-restricted-syntax
    for (const i in contactType.role_areas) {
      if (contactType.role_areas[i].role_area === option.value) return this.getContactTypeLevelLabel(contactType.role_areas[i]);
    }
    return this.getLevelLabel(SPPContactTypeLevelsEnum.general);
  }

  manageDeleteContactType(contactType: ContactType) {
    const { t } = this.props;

    const initial: SPPContactTypeRoleAreas[] = [];
    const previousValue = (!contactType.role_areas) ? [] : contactType.role_areas.reduce<SPPContactTypeRoleAreas[]>(
      (previousValue: SPPContactTypeRoleAreas[], currentValue: ContactTypeRole): SPPContactTypeRoleAreas[] => {
        previousValue.push(currentValue.role_area);
        return previousValue;
      },
      initial,
    );

    const losingCertificates = this.losingCertificates(previousValue || [], []);
    if (losingCertificates) {
      this.props.confirmDialog(
        t('Certifications exist for {{contactFirstName}} {{contactLastName}} specific to Mechanic or Tire Technician and will be removed.', {
          contactFirstName: this.props.contact.first_name,
          contactLastName: this.props.contact.last_name,
        }),
        t('Losing Certificates'),
        t('Ok'),
        t('Cancel'),
        async () => {
          await this.updateRoleAreasValue([], contactType);
        },
        undefined,
        { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
      );

      return;
    }

    const michelinContactTypes = hasMichelinContactTypes([contactType], true);
    if (michelinContactTypes.length) {
      this.props.confirmDialog(
        // eslint-disable-next-line max-len
        t('One or more contact types assigned to {{contactFirstName}} {{contactLastName}}  have priority levels which must be reassigned to another contact at the location in order to complete this change. How do you wish to proceed?', {
          contactFirstName: this.props.contact.first_name,
          contactLastName: this.props.contact.last_name,
        }),
        t('Reassign priority levels and contact types'),
        t('Yes, Reassign'),
        t('No, Undo'),
        () => {
          this.setState({ reassignModalContact: this.props.contact });
        },
        undefined,
        { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
      );
    } else if (hasContactTypePriorityLevelsAssigned([contactType])) {
      this.props.confirmDialog(
        t('Do you wish to reassign the priority levels and contact type(s) to a different contact prior to deletion?'),
        t('Reassign priority levels and contact types'),
        t('Yes, Reassign'),
        t('No, Delete'),
        () => {
          this.setState({ reassignModalContact: this.props.contact });
        },
        () => this.deleteContactType(contactType),
        { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
      );
    } else {
      this.props.confirmDialog(
        t('Are you sure you want to remove the contact type(s)?'),
        t('Remove contact type'),
        t('Remove contact type'),
        t('Keep contact type'),
        () => this.deleteContactType(contactType),
        undefined,
        { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
      );
    }
  }

  deleteContactType(contactType: ContactType) {
    const contactTypes: ContactType[] = [];

    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in this.state.listData) {
      const newCType = this.state.listData[i];
      if (newCType.id === contactType.id) {
        newCType.role_areas = [];
      }
      contactTypes.push(newCType);
    }
    contactTypes.sort((a, b) => {
      if (a.service < b.service) return -1;
      if (a.service > b.service) return 1;
      return 0;
    });
    this.setState({ listData: contactTypes });
    this.forceUpdate();
        this.props.onChange?.(contactTypes);
  }

  /// //////////////////////////////////////////////////////////////////
  /// Get Info METHODS
  /// //////////////////////////////////////////////////////////////////

  // eslint-disable-next-line class-methods-use-this
  getServiceLabel(service: string): string {
    let str = service;
    const contact_types_services: any = getOptions('dealer_dealer_contact_types_services');
    contact_types_services.forEach((v: any) => {
      if (v.value === service) str = v.label;
    });
    return str;
  }

  getContactTypeServiceLabel(contactType: ContactType): string {
    return this.getServiceLabel(contactType.service);
  }

  // eslint-disable-next-line class-methods-use-this
  getLevelLabel(level: string): string {
    let str = level;
    const contact_types_levels: any = getOptions('dealer_dealer_contact_types_levels');
    contact_types_levels.forEach((v: any) => {
      if (v.value === level) str = v.label;
    });
    return str;
  }

  getContactTypeLevelLabel(role: ContactTypeRole) {
    const level = (role.levels?.length) ? role.levels[0].level : SPPContactTypeLevelsEnum.general;
    return this.getLevelLabel(level);
  }

  // eslint-disable-next-line class-methods-use-this
  getRoleAreaLabel(roleArea: string): string {
    let str = roleArea;
    const contact_types_role_areas: any = getOptions('dealer_dealer_contact_types_role_areas');
    contact_types_role_areas.forEach((v: any) => {
      if (v.value === roleArea) str = v.label;
    });
    return str;
  }

  getContactTypeRoleAreaLabel(role: ContactTypeRole): string {
    return this.getRoleAreaLabel(role.role_area);
  }

  /// //////////////////////////////////////////////////////////////////
  /// Other METHODS
  /// //////////////////////////////////////////////////////////////////

  afterPriorityLevelsSave() {
    this.setState({ assignModalContact: null, reassignModalContact: null });
    this.props.afterSave();
    this.forceUpdate();
  }

  isModified(): boolean {
    return JSON.stringify(this.originalData) !== JSON.stringify(this.state.listData);
  }

  // Duplicate on Contact/Detail.tsx
  // Duplicate on Contact/List.tsx
  // eslint-disable-next-line class-methods-use-this
  hasContactTypePriorityLevels(contactTypes: ContactType[] | undefined): boolean {
    if (!contactTypes || !contactTypes.length) return false;
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in contactTypes) {
      const roleAreas = contactTypes[i].role_areas;
      if (roleAreas.length) return true;
    }
    return false;
  }
}

const ContactTypesListWithStuff = withTranslation()(withDialogs(ContactTypesList));

export {
  ContactTypesListWithStuff as ContactTypesList,
};
